import type { Visit } from './visits'
import {
  ACQUISITION_CATEGORIES,
  ACQUISITION_ITEMS,
  CATEGORY_LABELS,
  CATEGORY_DESCRIPTIONS,
  getItemsByCategory,
  getTotalItemCount,
  type AcquisitionCategory,
} from './acquisitionAssessment'

export type AssessmentWorkflow = 'acquisition' | 'service_line'

export function getAssessmentRoute(workflow: AssessmentWorkflow): string {
  return workflow === 'acquisition' ? '/assessments/acquisition' : '/assessments/service-line'
}

export function getAssessmentContext(workflow: AssessmentWorkflow): {
  title: string
  creationLabel: string
  executionLabel: string
  framing: string
  executionUrl: (visitId: string) => string
} {
  if (workflow === 'acquisition') {
    return {
      title: 'Acquisition Due Diligence',
      creationLabel: 'Create Due-Diligence Assessment',
      executionLabel: 'Execute Due-Diligence Checklist',
      framing: 'Risk framing emphasizes compliance liabilities and remediation exposure before transaction close.',
      executionUrl: (visitId: string) => `/assessments/acquisition/${visitId}`,
    }
  }

  return {
    title: 'Service-Line Expansion Assessment',
    creationLabel: 'Create Service-Line Assessment',
    executionLabel: 'Execute Service-Line Checklist',
    framing: 'Readiness framing emphasizes operational preparedness and launch-risk controls for expansion.',
    executionUrl: (visitId: string) => `/visits/${visitId}/review`,
  }
}

/**
 * Get the acquisition DD checklist structure for display.
 */
export function getAcquisitionChecklistOverview(): {
  totalItems: number
  categories: Array<{
    key: AcquisitionCategory
    label: string
    description: string
    itemCount: number
  }>
} {
  return {
    totalItems: getTotalItemCount(),
    categories: ACQUISITION_CATEGORIES.map((cat) => ({
      key: cat,
      label: CATEGORY_LABELS[cat],
      description: CATEGORY_DESCRIPTIONS[cat],
      itemCount: getItemsByCategory(cat).length,
    })),
  }
}

export { ACQUISITION_ITEMS, ACQUISITION_CATEGORIES, CATEGORY_LABELS }
export type { AcquisitionCategory }

export function summarizeAssessmentReadiness(visits: Visit[]): {
  total: number
  in_progress: number
  released: number
  readiness: 'high_risk' | 'watch' | 'ready'
} {
  const total = visits.length
  const in_progress = visits.filter((visit) => visit.status === 'in_progress' || visit.status === 'scheduled').length
  const released = visits.filter((visit) => visit.status === 'released').length
  const readiness = in_progress > released ? 'watch' : released > 0 ? 'ready' : 'high_risk'

  return {
    total,
    in_progress,
    released,
    readiness,
  }
}
